import * as THREE from 'three'
import { CivicScene } from './CivicScene'
import { CivicNeighborhood } from './CivicNeighborhood'
import { civicTrafficPose } from './civicWorlds'
import { parisMansardGeometry } from './parisArchitecture'

const cream='#e4d8b8',ochre='#d1b175',tile='#9e6449',timber='#825344',stone='#b5b5a1',green='#627e6a'
/** Hanoi landmarks share the established parcel, camera and visibility rules. */
export class HanoiScene extends CivicScene {
  private mansard=parisMansardGeometry()
  private openings=new Map<string,THREE.BufferGeometry>()
  constructor(neighborhood?:CivicNeighborhood){super('hanoi',neighborhood);this.geometries.add(this.mansard)}
  private opening(x:number,y:number,z:number,w:number,h:number,color=cream){
    const key=`${w}-${h}`;let geometry=this.openings.get(key)
    if(!geometry){const s=new THREE.Shape(),r=w/2;s.moveTo(-r,0);s.lineTo(r,0);s.lineTo(r,h-r);s.absarc(0,h-r,r,0,Math.PI,false);s.closePath();geometry=new THREE.ExtrudeGeometry(s,{depth:.075,bevelEnabled:false,curveSegments:18});this.openings.set(key,geometry);this.geometries.add(geometry)}
    this.add(geometry,this.mat('#59665b'),x,y,z,1,1,1);this.arch(x,y,z+.1,w,h,.12,color)
  }
  private templeRoof(x:number,y:number,z:number,w:number,d:number,h:number){
    this.roof(x,y,z,w,d,h)
    for(const sx of [-1,1])for(const sz of [-1,1]){
      this.beam(new THREE.Vector3(x+sx*w*.4,y+.1,z+sz*d*.4),new THREE.Vector3(x+sx*w*.5,y+.34,z+sz*d*.5),.06,tile)
      this.beam(new THREE.Vector3(x+sx*w*.5,y+.34,z+sz*d*.5),new THREE.Vector3(x+sx*w*.49,y+.62,z+sz*d*.51),.055,tile)
    }
    this.ball(x,y+h+.25,z,.17,ochre)
  }
  private hall(x:number,z:number,w:number,d:number,h:number){
    this.box(x,.7,z,w+.6,.45,d+.5,cream);this.box(x,h/2+.85,z,w*.88,h,d*.86,ochre)
    for(let face=0;face<4;face++)this.at(x,z,1,face*Math.PI/2,()=>{
      const ww=face%2?d:w,zz=(face%2?w:d)/2,n=Math.max(1,Math.floor(ww/1.35))
      for(let i=0;i<=n;i++)this.disc(-ww/2+i*ww/n,h/2+.85,zz,.085,h,timber)
      for(let i=0;i<n;i++){const xx=-ww/2+(i+.5)*ww/n;this.box(xx,h/2+.7,zz-.12,ww/n*.7,h*.68,.09,'#635044');for(let a=-.27;a<=.3;a+=.14)this.box(xx+a,h/2+.7,zz-.04,.025,h*.65,.045,'#bd956d')}
      for(const yy of [h+.65,h+.88])this.box(0,yy,zz,ww,.16,.24,timber)
    });this.templeRoof(x,h+1,z,w+1,d+1,Math.max(.9,w*.15))
  }
  private turtle(level:number){
    this.terrace();this.disc(0,.4,0,11.3,.13,'#74a59a');this.disc(0,.58,0,4.95,.28,'#8b9e77');this.box(0,.85,0,7.3,.28,5.7,stone)
    for(let tier=0;tier<3;tier++){
      const w=6.2-tier*1.65,d=4.5-tier*1.12,h=tier<2?2.45:1.7+level*.08,y=1+tier*2.65
      this.box(0,y+h/2,0,w,h,d,stone);this.box(0,y+h,0,w+.3,.23,d+.3,cream)
      for(let face=0;face<4;face++)this.at(0,0,1,face*Math.PI/2,()=>{
        const ww=face%2?d:w,zz=(face%2?w:d)/2+.015,n=tier===2?1:face%2?2:3
        for(let i=0;i<n;i++)this.opening(-ww/2+(i+.5)*ww/n,y+.22,zz,tier===2?.8:.86,tier===2?1.1:1.8,cream)
        for(const side of [-1,1])this.box(side*(ww/2-.12),y+h/2,zz+.08,.18,h,.16,cream)
      })
    }
    this.templeRoof(0,8.1+level*.08,0,3.5,2.7,1.15)
    for(const x of [-10,10])for(const z of [-8,8])this.tree(x,z,.72)
    for(const s of [-1,1])for(let i=0;i<6;i++)this.box(s*10.3,.59,-5+i*2,1.1,.13,1.7,cream)
  }
  private literature(level:number){
    this.terrace();this.box(0,.4,-.4,8,.12,8,'#7b9d91');for(const s of [-1,1])this.box(s*4.4,.45,-.4,.6,.18,9,cream)
    this.hall(0,-7.7,15.6,4.1,2.8+level*.12)
    for(const s of [-1,1])this.at(s*8.8,-.3,1,Math.PI/2,()=>this.hall(0,0,9.9,2.6,1.7))
    // Khuê Văn pavilion: four stone piers, red upper storey, circular windows.
    for(const x of [-1.25,1.25])for(const z of [6.1,8.6])this.box(x,1.9,z,.38,2.9,.38,cream)
    this.box(0,3.3,7.35,3.35,.32,3.35,cream);this.box(0,4.5,7.35,2.75,2.1,2.75,'#a45b46')
    for(let face=0;face<4;face++)this.at(0,7.35,1,face*Math.PI/2,()=>{
      const q=new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1,0,0),Math.PI/2)
      this.add(this.cylinder,this.mat(cream),0,4.56,1.42,.72,.09,.72,0,q);this.add(this.cylinder,this.mat('#60584a'),0,4.56,1.49,.58,.075,.58,0,q)
      for(let i=0;i<8;i++){const a=i*Math.PI/4;this.beam(new THREE.Vector3(0,4.56,1.54),new THREE.Vector3(Math.cos(a)*.6,4.56+Math.sin(a)*.6,1.54),.025,ochre)}
    })
    this.templeRoof(0,5.65,7.35,4.7,4.7,1.3);this.templeRoof(0,6.75,7.35,3.2,3.2,1.25)
    for(const x of [-6,6])for(let z=-2;z<=3;z+=1.7){this.ball(x,.61,z,.43,'#9a9d85',.42);this.box(x,1.04,z,.48,.8,.16,'#b2b2a0')}
    for(const x of [-10,10])this.tree(x,9,.55)
  }
  private opera(level:number){
    this.terrace();const h=6.9+level*.18
    this.box(0,h/2+.6,-1,18,h,12,ochre);this.box(0,.8,-1,19,.6,13,cream)
    for(const y of [3.2,h+.7])this.box(0,y,-1,18.5,.26,12.5,cream)
    this.add(this.mansard,this.mat('#667e7a'),0,h+1.65,-1,18.7,2,12.7)
    for(const side of [-1,1])this.at(side*9.05,-1,1,side*Math.PI/2,()=>{for(let x=-4.8;x<=4.9;x+=1.9){this.opening(x,1,.03,.86,1.9);this.window(x,4.8,.08,.88,1.6,true);this.box(x+.7,4.1,.13,.18,5.2,.2,cream)}})
    this.box(0,4.2,5.25,10.8,6.5,1.3,ochre)
    for(const x of [-4,-2,0,2,4]){this.opening(x,1,6,.9,2.1);this.opening(x,3.6,6,1,2.3)}
    for(const x of [-5,-3,-1,1,3,5]){this.disc(x,4.9,6.37,.17,3.2,cream);this.box(x,6.6,6.37,.55,.28,.55,cream)}
    this.box(0,3.2,6.15,11.7,.22,1.5,cream);this.box(0,6.85,6.1,12,.4,1.7,cream)
    for(let x=-5.4;x<=5.5;x+=.32)this.box(x,3.61,6.81,.08,.63,.1,cream)
    const pediment=new THREE.Shape();pediment.moveTo(-6,0);pediment.lineTo(6,0);pediment.lineTo(0,2.05);pediment.closePath();const g=new THREE.ExtrudeGeometry(pediment,{depth:.5,bevelEnabled:false});this.geometries.add(g);this.add(g,this.mat(cream),0,7.02,5.95,1,1,1)
    this.beam(new THREE.Vector3(-6,7.05,6.52),new THREE.Vector3(0,9.1,6.52),.075,ochre);this.beam(new THREE.Vector3(0,9.1,6.52),new THREE.Vector3(6,7.05,6.52),.075,ochre)
    this.sign('NHÀ HÁT LỚN',0,6.84,7.02,4.4,.36,ochre);this.steps(0,9,13,6);for(const x of [-10,10])this.lamp(x,9)
  }
  private longBien(level:number){
    this.terrace();this.box(0,.4,0,23,.12,23,'#739b99')
    for(const x of [-7.6,0,7.6]){this.box(x,1.8,0,.9,2.65,4.2,stone);this.box(x,3.15,0,1.2,.3,4.7,cream)}
    this.box(0,3.4,0,23,.32,4.8,'#82917e');for(const z of [-.65,.65])this.box(0,3.61,z,23,.08,.09,'#776d58')
    for(let x=-11;x<=11;x+=.48)this.box(x,3.57,0,.12,.06,1.8,'#a8997a')
    for(const side of [-1,1])for(let span=0;span<3;span++){
      const start=-11.4+span*7.6,z=side*1.5,ys=[.4,2.7,3.9,4.6,3.9,2.7,.4].map(v=>v+3.6+level*.06)
      for(let i=0;i<6;i++){const x=start+i*7.6/6,xx=start+(i+1)*7.6/6;this.beam(new THREE.Vector3(x,ys[i],z),new THREE.Vector3(xx,ys[i+1],z),.1,green);this.beam(new THREE.Vector3(x,3.6,z),new THREE.Vector3(xx,ys[i+1],z),.055,green);this.beam(new THREE.Vector3(x,ys[i],z),new THREE.Vector3(xx,3.6,z),.055,green);this.box(x,(ys[i]+3.6)/2,z,.11,ys[i]-3.6,.12,green)}
      this.box(start+3.8,3.65,z,7.6,.15,.15,green)
    }
    for(let x=-10;x<=10;x+=2.5){const local=((x+11.4)%7.6)/7.6*6,i=Math.floor(local),heights=[.4,2.7,3.9,4.6,3.9,2.7,.4],y=3.6+heights[i]+(heights[i+1]-heights[i])*(local-i)+level*.06;this.box(x,y,0,.08,.1,3,green)}
    for(const side of [-1,1]){this.box(0,3.84,side*2.3,23,.12,.06,cream);for(let x=-11;x<=11;x+=.7)this.box(x,3.67,side*2.3,.04,.42,.06,cream)}
    for(const x of [-10,10])for(const z of [-10,10])this.tree(x,z,.65)
  }
  private lotteHanoi(level:number){
    this.terrace();this.box(0,1.2,0,14,1.6,11,'#c4c4b2');const h=30+level*.9
    // Two subtly flaring glass wings, separated by a recessed vertical seam.
    for(const side of [-1,1])for(let i=0;i<22;i++){
      const t=i/22,hh=h/22,w=3.35-.4*t+.55*Math.pow(t,5),x=side*(1.93+.26*Math.sin(t*Math.PI)),y=2+i*hh,z=side*.32
      this.box(x,y+hh/2,z,w,hh+.025,5.4,this.facade(side<0?'blue':'teal'));this.box(x,y,z,w+.08,.08,5.52,'#b8cdc5')
      for(const face of [-1,1])for(let k=-3;k<=3;k++)this.box(x+k*w/7,y+hh/2,z+face*2.74,.035,hh,.055,cream)
    }
    this.box(0,2+h/2,0,.42,h,4.6,'#355e70')
    for(const side of [-1,1]){this.box(side*2.03,h+2.5,side*.32,3.5,.8,5.5,'#a5c0ba');this.beam(new THREE.Vector3(side*.35,h+2.9,side*.32-2.7),new THREE.Vector3(side*3.75,h+4.3,side*.32-2.7),.08,cream)}
    this.sign('LOTTE',0,1.28,5.58,4,.6,cream);this.gardens()
  }
  protected override landmark(id:number,level:number){[()=>this.turtle(level),()=>this.literature(level),()=>this.opera(level),()=>this.longBien(level),()=>this.lotteHanoi(level)][id]()}
  private scooter(index:number){
    const root=new THREE.Group(),paint=index%2?'#b76855':'#5e898a',wheelQ=new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0,0,1),Math.PI/2)
    for(const z of [-.38,.38])this.add(this.cylinder,this.mat('#39443d'),0,.22,z,.18,.095,.18,0,wheelQ)
    this.box(0,.36,0,.23,.19,.72,paint);this.box(0,.59,-.1,.25,.12,.4,'#46534a');this.box(0,.57,.31,.25,.42,.16,paint);this.box(0,.85,.29,.42,.045,.045,'#687c72')
    this.box(0,.85,-.12,.24,.37,.2,index%2?'#c29a70':'#839c87');this.ball(0,1.17,-.12,.15,'#d4b18b');this.ball(0,1.24,-.12,.16,paint,.65)
    for(const s of [-1,1]){this.beam(new THREE.Vector3(s*.13,.94,-.1),new THREE.Vector3(s*.18,.84,.27),.04,'#c9aa82');this.box(s*.15,.46,-.04,.075,.3,.1,'#50685c')}
    this.flush(root);return root
  }
  protected override traffic(){for(const lane of [-15.95,-14.45,14.45,15.95])for(let i=0;i<8;i++){
    const node=i%3===0?this.car(i):this.scooter(i);this.root.add(node);this.motions.push({node,update:t=>{const p=civicTrafficPose(t,i,lane,'hanoi');node.position.set(p.x,.17,p.z);node.rotation.y=p.angle;node.scale.setScalar(p.scale)}})
  }}
}
